"use client"

/**
 * useLeoThread — shared conversation state for the two Leo surfaces that
 * render a thread today:
 *
 *   1. `LeoLandingClient` — the focused `/<product>/leo` canvas. Empty
 *      state shows the hero composer; first send transitions the page
 *      into an inline conversation.
 *   2. `AskLeoProvider` — the panel/window shell thread opened by ⌘⌥K and
 *      inline KPI/chart triggers. Persists for the session until New chat;
 *      recents on the landing route are separate.
 *
 * The hook owns the message list and the mock streaming timeout. Scroll
 * pinning is handled by `MessageScroller` in `LeoThreadMessages`. Today the
 * assistant response is a stub (`mockAssistantReply`); when a real model is
 * wired the API call goes here and both surfaces inherit it for free.
 */

import * as React from "react"

export type LeoThreadMessage = {
  id: string
  role: "user" | "assistant"
  content: string
  /** Assistant-only: show thinking animation until the reply is applied. */
  pending?: boolean
  /**
   * Assistant-only: rendered under the sentence. Answers that hand over
   * something to act on — draft cards, a small table — put it here rather than
   * flattening it into prose the author then has to retype.
   */
  attachment?: React.ReactNode
  /** Assistant-only: catalog cards stay visible but no longer accept taps. */
  attachmentFrozen?: boolean
  /** Assistant-only: which suggestion set was applied from the catalog. */
  appliedAlternateId?: string
  /**
   * Assistant-only: render the composer hero catalog from page context until
   * snapshotted into `attachment` on the next user turn.
   */
  heroCatalogLive?: boolean
  /** Assistant-only: one-tap next turns, e.g. "Make it harder". */
  followUps?: string[]
}

export interface LeoThreadReply {
  content: string
  attachment?: React.ReactNode
  followUps?: string[]
  heroCatalogLive?: boolean
}

/**
 * How a surface answers its own turns. The composer knows what a question is
 * and what the author has typed so far; the panel only knows how to render a
 * conversation, so the page that owns the domain owns the answer.
 */
export type LeoThreadResponder = (prompt: string) => LeoThreadReply

/** Visible thinking window before a reply lands in the thread. */
export const LEO_REPLY_DELAY_MS = 3500

/** Hero apply: logo + lazy loader in the thread before canvas fields update. */
export const HERO_APPLY_THINKING_MS = 900

/** Wave loader beat before assistant-only replies (variation chips, etc.). */
export const LEO_LAZY_REPLY_MS = HERO_APPLY_THINKING_MS

function hasHeroPagerLead(messages: LeoThreadMessage[]): boolean {
  return messages.some(
    (message) =>
      message.role === "assistant" &&
      (message.heroCatalogLive || message.attachment),
  )
}

function freezeLiveHeroRows(
  messages: LeoThreadMessage[],
  attachment: React.ReactNode,
  appliedAlternateId?: string,
): LeoThreadMessage[] {
  return messages.map((message) =>
    message.role === "assistant" &&
    message.heroCatalogLive &&
    !message.attachmentFrozen
      ? {
          ...message,
          heroCatalogLive: false,
          attachment,
          attachmentFrozen: true,
          appliedAlternateId: appliedAlternateId ?? message.appliedAlternateId,
        }
      : message,
  )
}

/** Pager cards always lead the thread — prepend when a chip beats the seed effect. */
function ensureHeroPagerLead(
  messages: LeoThreadMessage[],
  attachment: React.ReactNode,
  appliedAlternateId?: string,
): LeoThreadMessage[] {
  if (hasHeroPagerLead(messages)) {
    return freezeLiveHeroRows(messages, attachment, appliedAlternateId)
  }
  return [
    {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "",
      heroCatalogLive: false,
      attachment,
      attachmentFrozen: true,
      appliedAlternateId,
    },
    ...messages,
  ]
}

function scheduleAssistantReply(
  setMessages: React.Dispatch<React.SetStateAction<LeoThreadMessage[]>>,
  pendingReplyTimeoutsRef: React.MutableRefObject<number[]>,
  asstId: string,
  reply: LeoThreadReply,
  delayMs = LEO_REPLY_DELAY_MS,
) {
  const tid = window.setTimeout(() => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === asstId && m.role === "assistant"
          ? { ...m, ...reply, pending: false }
          : m,
      ),
    )
    pendingReplyTimeoutsRef.current =
      pendingReplyTimeoutsRef.current.filter((t) => t !== tid)
  }, delayMs)
  pendingReplyTimeoutsRef.current.push(tid)
}

export function mockAssistantReply(userText: string): string {
  const trimmed =
    userText.length > 120 ? `${userText.slice(0, 120)}…` : userText
  return `Thanks — I received: "${trimmed}". Wire your assistant API here to return a real answer.`
}

export interface UseLeoThreadOptions {
  /**
   * Called every time the user submits a turn — typically used by the
   * landing surface to push the prompt onto the persisted recents list.
   * Stable identity required (memoize at the call site).
   */
  onUserTurn?: (prompt: string) => void
  /**
   * Answers every turn while it is supplied. Falls back to the stub reply, so a
   * surface that has nothing domain-specific to say still behaves.
   */
  responder?: LeoThreadResponder
}

export interface UseLeoThread {
  messages: LeoThreadMessage[]
  isThinking: boolean
  /** Send a prompt — trims, no-ops on blank. Returns the user message row. */
  send: (prompt: string) => LeoThreadMessage | null
  /**
   * Send after freezing the live hero pager into its thread row in the same
   * commit — user turn and thinking always land below the pager cards.
   */
  sendAfterHeroSnapshot: (
    prompt: string,
    attachment: React.ReactNode,
    appliedAlternateId?: string,
  ) => LeoThreadMessage | null
  /** Record an exchange — user turn now, assistant reply after thinking. */
  appendExchange: (user: string, reply: LeoThreadReply) => void
  /** Same as appendExchange but snapshots the hero pager in the same commit. */
  appendExchangeAfterHeroSnapshot: (
    user: string,
    reply: LeoThreadReply,
    attachment: React.ReactNode,
    appliedAlternateId?: string,
  ) => void
  /** Same as appendExchange but lands immediately (no pending thinking row). */
  appendExchangeImmediate: (user: string, reply: LeoThreadReply) => void
  /**
   * User turn now, pending assistant until `completeExchange` resolves it.
   * Used when the reply timing is owned elsewhere (hero apply handoff).
   */
  beginExchange: (user: string) => string | null
  completeExchange: (asstId: string, reply: LeoThreadReply) => void
  /** Record one assistant line (e.g. the finding Leo opened on). */
  appendAssistant: (reply: LeoThreadReply) => void
  /** Insert the hero pager as the first assistant turn when missing. */
  prependHeroCatalog: (reply: LeoThreadReply) => void
  /** Pending wave loader, then the assistant reply (no user turn). */
  appendAssistantLazy: (reply: LeoThreadReply, delayMs?: number) => void
  /** Lock suggestion cards in place once the author picks one. */
  freezeCatalogAttachments: (
    appliedAlternateId: string,
    followUps?: string[],
  ) => void
  /** Reopen the latest frozen suggestion card set after Undo. */
  unfreezeLatestCatalogAttachment: () => void
  /** Copy the live hero catalog into the thread row before a follow-up turn. */
  snapshotHeroCatalog: (
    attachment: React.ReactNode,
    appliedAlternateId?: string,
  ) => void
  /** Cancel in-flight reply timers and drop pending assistant rows. */
  stop: () => void
  /** Reset the thread + cancel any pending mock reply timers. */
  reset: () => void
  /** Imperative scroll anchor — attach to the scrollable conversation container. */
  scrollRef: React.RefObject<HTMLDivElement | null>
}

export function useLeoThread(options: UseLeoThreadOptions = {}): UseLeoThread {
  const { onUserTurn, responder } = options
  // Read through a ref so a surface that rebuilds its responder mid-thread does
  // not have to keep `send` stable for the composer.
  const responderRef = React.useRef(responder)
  React.useEffect(() => {
    responderRef.current = responder
  }, [responder])
  const [messages, setMessages] = React.useState<LeoThreadMessage[]>([])
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const pendingReplyTimeoutsRef = React.useRef<number[]>([])

  const clearPendingReplyTimeouts = React.useCallback(() => {
    pendingReplyTimeoutsRef.current.forEach(clearTimeout)
    pendingReplyTimeoutsRef.current = []
  }, [])

  const send = React.useCallback<UseLeoThread["send"]>(
    (prompt) => {
      const trimmed = prompt.trim()
      if (!trimmed) return null
      onUserTurn?.(trimmed)
      const userId = crypto.randomUUID()
      const asstId = crypto.randomUUID()
      const userRow: LeoThreadMessage = {
        id: userId,
        role: "user",
        content: trimmed,
      }
      setMessages((prev) => [
        ...prev,
        userRow,
        { id: asstId, role: "assistant", content: "", pending: true },
      ])
      const tid = window.setTimeout(() => {
        let reply: LeoThreadReply
        try {
          reply = responderRef.current
            ? responderRef.current(trimmed)
            : { content: mockAssistantReply(trimmed) }
        } catch {
          reply = {
            content:
              "I can change the format, apply an open check, or suggest wording. Ask for one of those, or type in the field.",
          }
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === asstId && m.role === "assistant"
              ? { ...m, ...reply, pending: false }
              : m
          )
        )
        pendingReplyTimeoutsRef.current =
          pendingReplyTimeoutsRef.current.filter((t) => t !== tid)
      }, LEO_REPLY_DELAY_MS)
      pendingReplyTimeoutsRef.current.push(tid)
      return userRow
    },
    [onUserTurn]
  )

  const sendAfterHeroSnapshot = React.useCallback<
    UseLeoThread["sendAfterHeroSnapshot"]
  >(
    (prompt, attachment, appliedAlternateId) => {
      const trimmed = prompt.trim()
      if (!trimmed) return null
      onUserTurn?.(trimmed)
      const userId = crypto.randomUUID()
      const asstId = crypto.randomUUID()
      const userRow: LeoThreadMessage = {
        id: userId,
        role: "user",
        content: trimmed,
      }
      setMessages((prev) => [
        ...ensureHeroPagerLead(prev, attachment, appliedAlternateId),
        userRow,
        { id: asstId, role: "assistant", content: "", pending: true },
      ])
      const tid = window.setTimeout(() => {
        let reply: LeoThreadReply
        try {
          reply = responderRef.current
            ? responderRef.current(trimmed)
            : { content: mockAssistantReply(trimmed) }
        } catch {
          reply = {
            content:
              "I can change the format, apply an open check, or suggest wording. Ask for one of those, or type in the field.",
          }
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === asstId && m.role === "assistant"
              ? { ...m, ...reply, pending: false }
              : m
          )
        )
        pendingReplyTimeoutsRef.current =
          pendingReplyTimeoutsRef.current.filter((t) => t !== tid)
      }, LEO_REPLY_DELAY_MS)
      pendingReplyTimeoutsRef.current.push(tid)
      return userRow
    },
    [onUserTurn],
  )

  const appendExchange = React.useCallback<UseLeoThread["appendExchange"]>(
    (user, reply) => {
      const trimmed = user.trim()
      if (!trimmed) return
      const asstId = crypto.randomUUID()
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "user", content: trimmed },
        { id: asstId, role: "assistant", content: "", pending: true },
      ])
      scheduleAssistantReply(
        setMessages,
        pendingReplyTimeoutsRef,
        asstId,
        reply,
      )
    },
    [],
  )

  const appendExchangeAfterHeroSnapshot = React.useCallback<
    UseLeoThread["appendExchangeAfterHeroSnapshot"]
  >((user, reply, attachment, appliedAlternateId) => {
    const trimmed = user.trim()
    if (!trimmed) return
    const asstId = crypto.randomUUID()
    setMessages((prev) => [
      ...ensureHeroPagerLead(prev, attachment, appliedAlternateId),
      { id: crypto.randomUUID(), role: "user", content: trimmed },
      { id: asstId, role: "assistant", content: "", pending: true },
    ])
    scheduleAssistantReply(
      setMessages,
      pendingReplyTimeoutsRef,
      asstId,
      reply,
    )
  }, [])

  const appendExchangeImmediate = React.useCallback<
    UseLeoThread["appendExchangeImmediate"]
  >((user, reply) => {
    const trimmed = user.trim()
    if (!trimmed) return
    setMessages((prev) => [
      ...prev,
      { id: crypto.randomUUID(), role: "user", content: trimmed },
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: reply.content,
        attachment: reply.attachment,
        followUps: reply.followUps,
        heroCatalogLive: reply.heroCatalogLive,
      },
    ])
  }, [])

  const beginExchange = React.useCallback<UseLeoThread["beginExchange"]>(
    (user) => {
      const trimmed = user.trim()
      if (!trimmed) return null
      const asstId = crypto.randomUUID()
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "user", content: trimmed },
        { id: asstId, role: "assistant", content: "", pending: true },
      ])
      return asstId
    },
    [],
  )

  const completeExchange = React.useCallback<UseLeoThread["completeExchange"]>(
    (asstId, reply) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === asstId && m.role === "assistant" && m.pending
            ? { ...m, ...reply, pending: false }
            : m,
        ),
      )
    },
    [],
  )

  const appendAssistant = React.useCallback<UseLeoThread["appendAssistant"]>(
    (reply) => {
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: reply.content,
          attachment: reply.attachment,
          followUps: reply.followUps,
          heroCatalogLive: reply.heroCatalogLive,
        },
      ])
    },
    [],
  )

  const prependHeroCatalog = React.useCallback<
    UseLeoThread["prependHeroCatalog"]
  >((reply) => {
    setMessages((prev) => {
      if (hasHeroPagerLead(prev)) return prev
      return [
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: reply.content,
          attachment: reply.attachment,
          followUps: reply.followUps,
          heroCatalogLive: reply.heroCatalogLive,
        },
        ...prev,
      ]
    })
  }, [])

  const appendAssistantLazy = React.useCallback<
    UseLeoThread["appendAssistantLazy"]
  >((reply, delayMs = LEO_LAZY_REPLY_MS) => {
    const asstId = crypto.randomUUID()
    setMessages((prev) => [
      ...prev,
      { id: asstId, role: "assistant", content: "", pending: true },
    ])
    scheduleAssistantReply(
      setMessages,
      pendingReplyTimeoutsRef,
      asstId,
      reply,
      delayMs,
    )
  }, [])

  const freezeCatalogAttachments = React.useCallback(
    (appliedAlternateId: string, followUps?: string[]) => {
      setMessages((prev) => {
        let lastOpenAttachmentIndex = -1
        for (let index = prev.length - 1; index >= 0; index -= 1) {
          const message = prev[index]
          if (
            message.role === "assistant" &&
            (message.attachment || message.heroCatalogLive) &&
            !message.attachmentFrozen
          ) {
            lastOpenAttachmentIndex = index
            break
          }
        }
        if (lastOpenAttachmentIndex < 0) return prev
        return prev.map((message, index) =>
          index === lastOpenAttachmentIndex
            ? {
                ...message,
                attachmentFrozen: true,
                appliedAlternateId,
                ...(followUps !== undefined ? { followUps } : {}),
              }
            : message,
        )
      })
    },
    [],
  )

  const unfreezeLatestCatalogAttachment = React.useCallback(() => {
    setMessages((prev) => {
      let lastFrozenIndex = -1
      for (let index = prev.length - 1; index >= 0; index -= 1) {
        const message = prev[index]
        if (
          message.role === "assistant" &&
          (message.attachment || message.heroCatalogLive) &&
          message.attachmentFrozen
        ) {
          lastFrozenIndex = index
          break
        }
      }
      if (lastFrozenIndex < 0) return prev
      return prev.map((message, index) =>
        index === lastFrozenIndex
          ? {
              ...message,
              attachmentFrozen: false,
              appliedAlternateId: undefined,
            }
          : message,
      )
    })
  }, [])

  const snapshotHeroCatalog = React.useCallback<
    UseLeoThread["snapshotHeroCatalog"]
  >((attachment, appliedAlternateId) => {
    setMessages((prev) =>
      freezeLiveHeroRows(prev, attachment, appliedAlternateId),
    )
  }, [])

  const stop = React.useCallback(() => {
    clearPendingReplyTimeouts()
    setMessages((prev) =>
      prev.filter((m) => !(m.role === "assistant" && m.pending))
    )
  }, [clearPendingReplyTimeouts])

  const reset = React.useCallback(() => {
    clearPendingReplyTimeouts()
    setMessages([])
  }, [clearPendingReplyTimeouts])

  // MessageScroller owns scroll pinning; scrollRef kept for API compatibility.
  // Do not clear reply timers on unmount. Strict Mode remounts restore the
  // pending assistant row and would leave Leo thinking with no timer. The shell
  // thread lives in AskLeoProvider and only resets on New chat.

  const isThinking = messages.some((m) => m.pending)

  return {
    messages,
    isThinking,
    send,
    sendAfterHeroSnapshot,
    appendExchange,
    appendExchangeAfterHeroSnapshot,
    appendExchangeImmediate,
    beginExchange,
    completeExchange,
    appendAssistant,
    prependHeroCatalog,
    appendAssistantLazy,
    freezeCatalogAttachments,
    unfreezeLatestCatalogAttachment,
    snapshotHeroCatalog,
    stop,
    reset,
    scrollRef,
  }
}
